import React, { useState, useEffect } from 'react';
import PrimaryNavigation from '../../components/ui/PrimaryNavigation';
import NavigationBreadcrumb from '../../components/ui/NavigationBreadcrumb';
import Icon from '../../components/AppIcon';
import api from '../../api/axios';


import PerformanceComparisonChart from './components/PerformanceComparisonChart';
import PerformanceHeatMap from './components/PerformanceHeatMap';
import AgentUtilizationPanel from './components/AgentUtilizationPanel';
import PerformanceAlertsPanel from './components/PerformanceAlertsPanel';
import DetailedPerformanceTable from './components/DetailedPerformanceTable';
import CreateAIAssistantModal from './components/CreateAIAssistantModal';
import AssistantCreationFlow from './components/AssistantCreationFlow';
import AgentConfigPage from './components/AgentConfigPage';

const AgentPerformance = () => {
  const [selectedAgents, setSelectedAgents] = useState(['agent-001', 'agent-002', 'agent-003']);
  const [performancePeriod, setPerformancePeriod] = useState('daily');
  const [benchmarkBaseline, setBenchmarkBaseline] = useState('team-average');
  const [comparisonMetric, setComparisonMetric] = useState('resolution');
  const [heatMapView, setHeatMapView] = useState('hourly');
  const [agentLeaderboardData, setAgentLeaderboardData] = useState([]);
  const [loadingAgents, setLoadingAgents] = useState(true);
  const [agentsError, setAgentsError] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showCreationFlow, setShowCreationFlow] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [selectedAgentConfig, setSelectedAgentConfig] = useState(null);
  const [isAgentDrawerOpen, setIsAgentDrawerOpen] = useState(false);


  const fetchAgents = async () => {
    try {
      setLoadingAgents(true);
      setAgentsError(null);

      const response = await api.get("/api/v1/dashboard/get-agents");

      if (response.data?.success && response.data?.data?.agents) {
        const transformedAgents = response.data.data.agents.map((agent, index) => ({
          id: agent.agentId || `agent-${index}`,
          name: agent.agentName || "Unnamed Agent",

          resolutionRate: agent.resolutionRate ?? 0,
          resolutionTrend: 0,

          avgCallTime: (() => {
            const s = Math.round(agent.avgCallDurationSeconds ?? 0);
            return `${Math.floor(s / 60)}m ${s % 60}s`;
          })(),
          avgCallTimeMinutes: Math.round((agent.avgCallDurationSeconds ?? 0) / 60 * 100) / 100,
          handleTimeTrend: 0,

          totalCalls: agent.totalCalls ?? 0,
          escalationRate: agent.escalationRate ?? 0,

          status: "active",

          inboundPhone: agent.inboundPhone,
          outboundPhone: agent.outboundPhone,
          whatsappNumber: agent.whatsappNumber,

          createdOn: agent.createdOn
        }));

        setAgentLeaderboardData(transformedAgents);
      }
    } catch (err) {
      console.error("Agent API Error:", err);
      setAgentsError("Failed to load agents");
    } finally {
      setLoadingAgents(false);
    }
  };

  useEffect(() => {
    fetchAgents();
  }, []);

  const hasNoAgents = !loadingAgents && agentLeaderboardData.length === 0 && !agentsError;

  // Auto-open create modal for new users with no agents
  useEffect(() => {
    if (hasNoAgents) {
      setShowCreateModal(true);
    }
  }, [hasNoAgents]);


  // const agentLeaderboardData = [
  // {
  //   id: "agent-001",
  //   name: "AI Agent Alpha",
  //   resolutionRate: 94.5,
  //   resolutionTrend: 2.3,
  //   avgHandleTime: "3m 45s",
  //   avgHandleTimeMinutes: 3.75,
  //   handleTimeTrend: -1.2,
  //   csatScore: 4.8,
  //   csatTrend: 0.3,
  //   totalCalls: 1247,
  //   escalationRate: 5.2,
  //   status: "active",
  //   inboundPhone: "5551234567",
  //   outboundPhone: "5559876543",
  //   whatsappNumber: "5551122334",
  //   createdOn: "2025-01-15"
  // },
  // {
  //   id: "agent-002",
  //   name: "AI Agent Beta",
  //   resolutionRate: 91.2,
  //   resolutionTrend: -0.8,
  //   avgHandleTime: "4m 12s",
  //   avgHandleTimeMinutes: 4.2,
  //   handleTimeTrend: 0.5,
  //   csatScore: 4.6,
  //   csatTrend: -0.1,
  //   totalCalls: 1089,
  //   escalationRate: 7.8,
  //   status: "active",
  //   inboundPhone: "5552345678",
  //   outboundPhone: "5558765432",
  //   whatsappNumber: "5552233445",
  //   createdOn: "2025-02-20"
  // },
  // {
  //   id: "agent-003",
  //   name: "AI Agent Gamma",
  //   resolutionRate: 89.7,
  //   resolutionTrend: 1.5,
  //   avgHandleTime: "3m 58s",
  //   avgHandleTimeMinutes: 3.97,
  //   handleTimeTrend: -0.3,
  //   csatScore: 4.7,
  //   csatTrend: 0.2,
  //   totalCalls: 1156,
  //   escalationRate: 9.1,
  //   status: "active",
  //   inboundPhone: "5553456789",
  //   outboundPhone: "5557654321",
  //   whatsappNumber: "5553344556",
  //   createdOn: "2025-03-10"
  // },
  // {
  //   id: "agent-004",
  //   name: "AI Agent Delta",
  //   resolutionRate: 92.8,
  //   resolutionTrend: 3.1,
  //   avgHandleTime: "3m 22s",
  //   avgHandleTimeMinutes: 3.37,
  //   handleTimeTrend: -1.8,
  //   csatScore: 4.9,
  //   csatTrend: 0.4,
  //   totalCalls: 1334,
  //   escalationRate: 4.3,
  //   status: "active",
  //   inboundPhone: "5554567890",
  //   outboundPhone: "5556543210",
  //   whatsappNumber: "5554455667",
  //   createdOn: "2025-04-05"
  // },
  // {
  //   id: "agent-005",
  //   name: "AI Agent Epsilon",
  //   resolutionRate: 87.3,
  //   resolutionTrend: -1.2,
  //   avgHandleTime: "4m 35s",
  //   avgHandleTimeMinutes: 4.58,
  //   handleTimeTrend: 1.1,
  //   csatScore: 4.4,
  //   csatTrend: -0.2,
  //   totalCalls: 967,
  //   escalationRate: 11.5,
  //   status: "active",
  //   inboundPhone: "5555678901",
  //   outboundPhone: "5555432109",
  //   whatsappNumber: "5555566778",
  //   createdOn: "2025-05-12"
  // },
  // {
  //   id: "agent-006",
  //   name: "AI Agent Zeta",
  //   resolutionRate: 93.6,
  //   resolutionTrend: 2.7,
  //   avgHandleTime: "3m 51s",
  //   avgHandleTimeMinutes: 3.85,
  //   handleTimeTrend: -0.9,
  //   csatScore: 4.8,
  //   csatTrend: 0.3,
  //   totalCalls: 1198,
  //   escalationRate: 5.9,
  //   status: "active",
  //   inboundPhone: "5556789012",
  //   outboundPhone: "5554321098",
  //   whatsappNumber: "5556677889",
  //   createdOn: "2025-06-18"
  // },
  // {
  //   id: "agent-007",
  //   name: "AI Agent Eta",
  //   resolutionRate: 90.4,
  //   resolutionTrend: 0.6,
  //   avgHandleTime: "4m 08s",
  //   avgHandleTimeMinutes: 4.13,
  //   handleTimeTrend: 0.2,
  //   csatScore: 4.5,
  //   csatTrend: 0.1,
  //   totalCalls: 1023,
  //   escalationRate: 8.7,
  //   status: "active",
  //   inboundPhone: "5557890123",
  //   outboundPhone: "5553210987",
  //   whatsappNumber: "5557788990",
  //   createdOn: "2025-07-22"
  // },
  // {
  //   id: "agent-008",
  //   name: "AI Agent Theta",
  //   resolutionRate: 88.9,
  //   resolutionTrend: -0.4,
  //   avgHandleTime: "4m 18s",
  //   avgHandleTimeMinutes: 4.3,
  //   handleTimeTrend: 0.7,
  //   csatScore: 4.6,
  //   csatTrend: 0.0,
  //   totalCalls: 1145,
  //   escalationRate: 10.2,
  //   status: "active",
  //   inboundPhone: "5558901234",
  //   outboundPhone: "5552109876",
  //   whatsappNumber: "5558899001",
  //   createdOn: "2025-08-30"
  // },
  // {
  //   id: "agent-009",
  //   name: "AI Agent Iota",
  //   resolutionRate: 95.1,
  //   resolutionTrend: 3.8,
  //   avgHandleTime: "3m 15s",
  //   avgHandleTimeMinutes: 3.25,
  //   handleTimeTrend: -2.1,
  //   csatScore: 4.9,
  //   csatTrend: 0.5,
  //   totalCalls: 1412,
  //   escalationRate: 3.8,
  //   status: "active",
  //   inboundPhone: "5559012345",
  //   outboundPhone: "5551098765",
  //   whatsappNumber: "5559900112",
  //   createdOn: "2025-09-14"
  // },
  // {
  //   id: "agent-010",
  //   name: "AI Agent Kappa",
  //   resolutionRate: 86.5,
  //   resolutionTrend: -1.9,
  //   avgHandleTime: "4m 42s",
  //   avgHandleTimeMinutes: 4.7,
  //   handleTimeTrend: 1.4,
  //   csatScore: 4.3,
  //   csatTrend: -0.3,
  //   totalCalls: 891,
  //   escalationRate: 12.8,
  //   status: "active",
  //   inboundPhone: "5550123456",
  //   outboundPhone: "5550987654",
  //   whatsappNumber: "5550011223",
  //   createdOn: "2025-10-08"
  // },
  // {
  //   id: "agent-011",
  //   name: "AI Agent Lambda",
  //   resolutionRate: 91.8,
  //   resolutionTrend: 1.3,
  //   avgHandleTime: "3m 55s",
  //   avgHandleTimeMinutes: 3.92,
  //   handleTimeTrend: -0.6,
  //   csatScore: 4.7,
  //   csatTrend: 0.2,
  //   totalCalls: 1067,
  //   escalationRate: 7.2,
  //   status: "active",
  //   inboundPhone: "5551357924",
  //   outboundPhone: "5559753108",
  //   whatsappNumber: "5551122556",
  //   createdOn: "2025-11-19"
  // },
  // {
  //   id: "agent-012",
  //   name: "AI Agent Mu",
  //   resolutionRate: 89.2,
  //   resolutionTrend: 0.8,
  //   avgHandleTime: "4m 05s",
  //   avgHandleTimeMinutes: 4.08,
  //   handleTimeTrend: 0.3,
  //   csatScore: 4.5,
  //   csatTrend: 0.1,
  //   totalCalls: 1134,
  //   escalationRate: 9.6,
  //   status: "active",
  //   inboundPhone: "5552468135",
  //   outboundPhone: "5558642097",
  //   whatsappNumber: "5552233778",
  //   createdOn: "2025-12-03"
  // }];


  const performanceChartData = [
  { time: "00:00", "agent-001": 92, "agent-002": 88, "agent-003": 85, "agent-004": 82, "agent-005": 79 },
  { time: "04:00", "agent-001": 93, "agent-002": 89, "agent-003": 87, "agent-004": 83, "agent-005": 80 },
  { time: "08:00", "agent-001": 95, "agent-002": 91, "agent-003": 89, "agent-004": 85, "agent-005": 82 },
  { time: "12:00", "agent-001": 96, "agent-002": 93, "agent-003": 91, "agent-004": 87, "agent-005": 84 },
  { time: "16:00", "agent-001": 94, "agent-002": 92, "agent-003": 90, "agent-004": 86, "agent-005": 83 },
  { time: "20:00", "agent-001": 93, "agent-002": 90, "agent-003": 88, "agent-004": 84, "agent-005": 81 }];


  const heatMapData = [
  [85, 87, 92, 94, 93, 91, 88, 86],
  [83, 86, 91, 95, 94, 92, 89, 84],
  [84, 88, 93, 96, 95, 93, 90, 85],
  [82, 85, 90, 94, 93, 91, 87, 83],
  [86, 89, 94, 97, 96, 94, 91, 87],
  [79, 82, 87, 91, 90, 88, 84, 80],
  [77, 80, 85, 89, 88, 86, 82, 78]];


  const utilizationData = [
  { id: "agent-001", name: "AI Agent Alpha", utilization: 85, activeConversations: 12, capacity: 15, status: "active" },
  { id: "agent-002", name: "AI Agent Beta", utilization: 72, activeConversations: 8, capacity: 12, status: "active" },
  { id: "agent-003", name: "AI Agent Gamma", utilization: 93, activeConversations: 15, capacity: 16, status: "active" },
  { id: "agent-004", name: "AI Agent Delta", utilization: 58, activeConversations: 6, capacity: 10, status: "active" },
  { id: "agent-005", name: "AI Agent Epsilon", utilization: 0, activeConversations: 0, capacity: 10, status: "inactive" }];


  const alertsData = [
  {
    id: "alert-001",
    severity: "critical",
    title: "High Escalation Rate",
    message: "Agent Epsilon escalation rate exceeded 15% threshold in the last hour",
    agentName: "AI Agent Epsilon",
    metric: "16.2%",
    timestamp: new Date(Date.now() - 300000)
  },
  {
    id: "alert-002",
    severity: "warning",
    title: "Increased Handle Time",
    message: "Average handle time increased by 18% compared to baseline",
    agentName: "AI Agent Delta",
    metric: "+18%",
    timestamp: new Date(Date.now() - 900000)
  },
  {
    id: "alert-003",
    severity: "warning",
    title: "CSAT Score Decline",
    message: "Customer satisfaction score dropped below 4.5 threshold",
    agentName: "AI Agent Beta",
    metric: "4.3/5.0",
    timestamp: new Date(Date.now() - 1800000)
  },
  {
    id: "alert-004",
    severity: "info",
    title: "Peak Performance",
    message: "Resolution rate reached new high of 96% during peak hours",
    agentName: "AI Agent Alpha",
    metric: "96%",
    timestamp: new Date(Date.now() - 3600000)
  }];


  const agentOptions = agentLeaderboardData?.map((agent) => ({
    value: agent?.id,
    label: agent?.name
  }));

  const periodOptions = [
  { value: 'hourly', label: 'Hourly' },
  { value: 'daily', label: 'Daily' },
  { value: 'weekly', label: 'Weekly' }];


  const baselineOptions = [
  { value: 'team-average', label: 'Team Average' },
  { value: 'top-performer', label: 'Top Performer' },
  { value: 'industry-standard', label: 'Industry Standard' },
  { value: 'custom', label: 'Custom Baseline' }];


  const metricOptions = [
  { value: 'resolution', label: 'Resolution Rate' },
  { value: 'handleTime', label: 'Avg Call Time' },
  { value: 'kbUsage', label: 'KB Utilization' }];


  const handleAgentSelection = (agentId) => {
    setSelectedAgents((prev) => {
      if (prev?.includes(agentId)) {
        return prev?.filter((id) => id !== agentId);
      }
      if (prev?.length >= 5) {
        return prev;
      }
      return [...prev, agentId];
    });
  };

  const handleOpenCreationFlow = (template = null) => {
    setSelectedTemplate(template);
    setShowCreateModal(false);
    setShowCreationFlow(true);
  };

  const handleCloseCreationFlow = () => {
    setShowCreationFlow(false);
    setSelectedTemplate(null);
  };

  const handleCreationComplete = (agentData) => {
    const newAgent = {
      id: agentData?.agentId || `agent-new-${Date.now()}`,
      name: agentData?.agentName || 'New AI Assistant',
      status: 'active',
      totalCalls: 0,
      resolutionRate: 0,
      escalationRate: 0,
      avgCallTime: '0m 0s',
      avgCallTimeMinutes: 0,
    };
    setSelectedAgentConfig(newAgent);
    setIsAgentDrawerOpen(true);
  };

  const handleAgentRowClick = (agent) => {
    setSelectedAgentConfig(agent);
    setIsAgentDrawerOpen(true);
  };

  const handleCloseAgentConfig = () => {
    setIsAgentDrawerOpen(false);
    setTimeout(() => setSelectedAgentConfig(null), 300);
  };

  return (
    <>
      <PrimaryNavigation />
      <div className="main-content">
        <div className="min-h-screen bg-background">
          <NavigationBreadcrumb />
          <main className="max-w-[1440px] mx-auto px-4 md:px-6 lg:px-8 py-6 md:py-8">
            <div className="mb-6 md:mb-8">
              <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 mb-6">
                <div>
                  <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold text-foreground mb-2">Agent Performance</h1>
                  <p className="text-sm md:text-base text-muted-foreground">
                    Real-time monitoring and comparative analysis of AI agent effectiveness
                  </p>
                </div>
                <div className="flex items-center gap-3">
                  <button
                    onClick={() => { if (typeof fbq === 'function') fbq('trackCustom', 'agentperformance-create-ai-assistant-clicked', { content_category: 'AgentPerformancePage' }); window.dataLayer?.push({ event: 'agentperformance_create_ai_assistant_clicked' }); setShowCreateModal(true); }}
                    className="flex items-center gap-2.5 px-5 py-2.5 bg-gradient-to-r from-violet-600 to-indigo-600 text-white rounded-xl font-semibold text-sm hover:from-violet-700 hover:to-indigo-700 shadow-md hover:shadow-lg transition-all duration-200 hover:scale-[1.02]"
                  >
                    <Icon name="Plus" size={18} />
                    <span>Create AI Assistant</span>
                  </button>
                </div>
              </div>

              




























            </div>

            














            <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 mb-6 md:mb-8">
              <div className="lg:col-span-8 space-y-6">
                <PerformanceComparisonChart
                  data={performanceChartData}
                  selectedAgents={selectedAgents}
                  metric={comparisonMetric} />

                <PerformanceHeatMap data={heatMapData} viewMode={heatMapView} />
              </div>

              <div className="lg:col-span-4 space-y-6">
                <AgentUtilizationPanel utilizationData={utilizationData} />
                <PerformanceAlertsPanel alerts={alertsData} />
              </div>
            </div>

            <DetailedPerformanceTable agents={agentLeaderboardData} onAgentClick={handleAgentRowClick} />
          </main>
        </div>
      </div>
      <CreateAIAssistantModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onOpenCreationFlow={handleOpenCreationFlow}
      />
      <AssistantCreationFlow
        isOpen={showCreationFlow}
        onClose={handleCloseCreationFlow}
        template={selectedTemplate}
        onAgentCreated={fetchAgents}
        onCreationComplete={handleCreationComplete}
      />
      {selectedAgentConfig && (
        <AgentConfigPage agent={selectedAgentConfig} onBack={handleCloseAgentConfig} isOpen={isAgentDrawerOpen} />
      )}
    </>);

};

export default AgentPerformance;